import { redirect, type LoaderFunction } from 'react-router';
import { apiAppLegacy } from '@/app/runtime';
import { isAuthenticated, useAuthStore } from '@/entities/auth';
import { hasUnagreedRequired, userConsentsQueryOptions } from '@/entities/user';
import { queryClient } from '@/shared/api';
import { isNative } from '@/shared/bridge';
import { logPendingTodo } from '@/shared/lib';
import {
  buildNavPath,
  INBOUND_TOKEN_KEY,
  type LoginReason,
  NAV_QUERY_KEYS,
} from '@/shared/navigation';
import { ROUTES } from '@/shared/routing';

export interface NavigationGuards {
  requireAuth: LoaderFunction;
  /** 구독 funnel 전용 인증 가드 — 미인증 시 `reason=subscription` 을 실어 로그인으로 보낸다. */
  requireAuthForSubscription: LoaderFunction;
  /** 필수 약관 미동의 시 동의 온보딩으로 보낸다. **`requireAuth` 뒤에** 합성한다(토큰 선행 필요). */
  requireConsent: LoaderFunction;
  requireGuest: LoaderFunction;
  requireNative: LoaderFunction;
}

/**
 * 인증 상태를 보기 전에 auth store 의 로드(localStorage / bridge secure store)를 기다린다.
 *
 * RR v7 은 부모·자식 loader 를 병렬 실행해서, 한 곳에서 부팅하면 race 가 난다. 그래서 가드마다
 * 직접 await 한다. 가드 3종이 공유하므로 모듈 레벨에 둔다.
 */
export async function ensureAuthBootstrapped(): Promise<void> {
  if (useAuthStore.persist.hasHydrated()) return;
  await new Promise<void>((resolve) => {
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      unsub();
      resolve();
    });
    // 리스너 등록 직전에 로드가 끝났을 수도 있어, 등록 후 한 번 더 확인한다.
    if (useAuthStore.persist.hasHydrated()) {
      unsub();
      resolve();
    }
  });
}

/**
 * 진입 URL 의 단기 토큰(`&t=`)을 처리한다. 결정 SSOT = ADR `inbound-token-auth`.
 *
 * `t` 가 있으면 (기존 세션 유무와 무관하게 항상) 교환을 시도하고, 성공·실패 무관 `t` 만 떼어
 * 깨끗한 URL 로 replace 한다. 교환과 인증 판정을 같은 가드 안에서 순서대로 해야 RR 의 병렬 loader
 * race 를 피한다 — root loader 였다면 자식 가드가 교환 전에 미인증으로 보고 로그인을 쏜다.
 *
 * [TODO:token-login] BE 교환 규약(D13/D14) 미정 — 지금은 strip + replace 만. 규약 확정 시
 * `t` strip 앞에 교환을 끼운다 (계정 전환 안전을 위해 아래를 반드시 함께):
 *   - **선-로그아웃** — 기존 세션이 살아있으면 교환 _전_ BE logout API(best-effort) +
 *     `useAuthStore.clear()` + `queryClient.clear()` 로 서버·클라 양쪽 세션을 끝낸다(기존 features/logout
 *     패턴). logout 실패해도 client clear 는 진행. 교환은 비동기라 진입 즉시 비워야 잔재가 안 남는다.
 *   - **항상 교환** — 세션 스킵 없음. 앱 `t` 가 권위라 잔재 세션보다 우선("스킵"은 계정 전환을 깨뜨림).
 *   - 성공 → `setTokens(새 계정)` → 진입.
 *   - 실패 → (이미 비워진 상태라) 미인증 → 로그인.
 *   - **조용한 전환** — 확인 화면 없음(교환 전엔 신원 불명 + 앱이 진입 주체). 근거는 ADR Why §.
 *   - ⚠️ 가드의 api 호출(logout·교환)은 composition-root 의 "가드는 api 비의존" 원칙과 충돌 →
 *     api 를 모듈 평가 아닌 런타임(late-binding/주입)으로 참조해 TDZ 순환을 피한다.
 */
async function consumeInboundToken(request: Request): Promise<Response | null> {
  const url = new URL(request.url);
  if (!url.searchParams.has(INBOUND_TOKEN_KEY)) return null;
  // [TODO:token-login] const t = url.searchParams.get(INBOUND_TOKEN_KEY);
  //   기존 세션 있으면 clear+queryClient.clear → 교환 → 성공 setTokens / 실패 로그인 (ADR inbound-token-auth)
  logPendingTodo('token-login', {
    trigger: 'page-enter',
    status: '진입 토큰(&t=)을 BE 교환 없이 strip+replace 만 — 토큰 기반 로그인 미배선',
    changes: [
      'BE: 단기 토큰 교환 규약(D13/D14) 확정',
      'FE: strip 전에 선-로그아웃(clear+queryClient.clear) → 토큰 교환 → 성공 setTokens/실패 로그인',
    ],
    unblockedBy: 'BE 토큰 교환 규약 확정 후',
    ctx: { pathname: url.pathname },
  });
  url.searchParams.delete(INBOUND_TOKEN_KEY);
  return redirect(`${url.pathname}${url.search}${url.hash}`);
}

/**
 * 미인증 → 로그인 redirect. 현재 내부 경로를 `redirectUrl` 로 붙여 로그인 후 복귀시킨다
 * (declarative 가드도 imperative `goToLogin` 과 동일하게 `buildNavPath` 로 조립).
 * `reason` 은 진입 맥락(구독 funnel 등) 운반 — `default`(생략)이 기본. 값 검증
 * (open-redirect·self-loop·reason 화이트리스트)은 읽기 측 `loginSearchSchema` SSOT 가 담당.
 */
function redirectToLogin(request: Request, reason?: LoginReason): Response {
  // 진입하려던 full URL 을 그대로 실어 보낸다 (안전 판정은 읽기 측 `isSafeRedirectUrl` — host/allowlist).
  return redirect(
    buildNavPath(ROUTES.AUTH_LOGIN, {
      [NAV_QUERY_KEYS.REDIRECT_URL]: request.url,
      [NAV_QUERY_KEYS.REASON]: reason,
    }),
  );
}

/**
 * 인증 가드 factory — 미인증 시 `reason` 을 실어 로그인으로 보낸다. `&t=` 진입 토큰은
 * 인증 판정 전 `consumeInboundToken` 으로 먼저 처리한다. 기본(앱) 진입은 `reason` 생략,
 * 구독 funnel 은 `'subscription'` 을 바인딩해 로그인 페이지가 카피를 분기하게 한다.
 */
function makeRequireAuth(reason?: LoginReason): LoaderFunction {
  return async ({ request }) => {
    await ensureAuthBootstrapped();
    const stripped = await consumeInboundToken(request);
    if (stripped) return stripped;
    return isAuthenticated() ? null : redirectToLogin(request, reason);
  };
}

/**
 * 필수 약관 미동의 → 동의 온보딩(`/auth/consent`) redirect. 판정 SSOT = `hasUnagreedRequired`
 * (선택 항목은 미동의여도 통과). 진입하려던 full URL 을 `redirectUrl` 로 실어 동의 완료 후 복귀시킨다.
 *
 * **인증 선행 전제** — 토큰 없이는 조회가 401 이라 항상 `requireAuth` 뒤에 합성한다.
 * 조회는 `ensureQueryData` 라 캐시가 있으면 재호출 없음(네비게이션마다 재실행되어도 무료),
 * 동의 제출 시 `useUpdateUserConsentsMutation` 의 invalidate 로 캐시가 갱신되어 게이트가 풀린다.
 *
 * **fail-open** — 조회 실패(네트워크·5xx)는 통과시킨다. 앱 본체 전 경로에 걸리는 가드라 fail-closed 면
 * 일시적 BE 장애가 화면 전체를 잠근다. 미동의 상태로 새어도 BE 가 최종 방어선(FE 는 유도 책임).
 *
 * api(`apiAppLegacy`)는 composition-root 가 router _뒤_ 에 만들어 모듈 eval 시점엔 미정의 →
 * **late-binding**(async 본문에서만 접근)으로 TDZ 순환을 피한다(`planGuard` 동일 원칙).
 */
const requireConsent: LoaderFunction = async ({ request }) => {
  await ensureAuthBootstrapped();
  let consents;
  try {
    consents = await queryClient.ensureQueryData(userConsentsQueryOptions(apiAppLegacy));
  } catch {
    return null;
  }
  if (!hasUnagreedRequired(consents)) return null;
  return redirect(
    buildNavPath(ROUTES.AUTH_CONSENT, { [NAV_QUERY_KEYS.REDIRECT_URL]: request.url }),
  );
};

/**
 * 라우트 가드(auth / consent / guest / native)를 만든다. requireAuth·requireNative 는 인증 판정 전에
 * `consumeInboundToken` 으로 `&t=` 를 먼저 처리한다 (requireGuest 는 제외).
 */
export function createNavigationGuards(): NavigationGuards {
  return {
    requireAuth: makeRequireAuth(),
    requireAuthForSubscription: makeRequireAuth('subscription'),
    requireConsent,
    requireGuest: async () => {
      await ensureAuthBootstrapped();
      return isAuthenticated() ? redirect(ROUTES.DASHBOARD) : null;
    },
    requireNative: async ({ request }) => {
      await ensureAuthBootstrapped();
      const stripped = await consumeInboundToken(request);
      if (stripped) return stripped;
      if (!isAuthenticated()) return redirectToLogin(request);
      if (!isNative()) return redirect(ROUTES.DASHBOARD);
      return null;
    },
  };
}
